<template>
  <div class="ride-connected-users mt-5" :dir="isHebrew ? 'rtl' : 'ltr'">
    <h5 class="mb-3" :style="{ textAlign: isHebrew ? 'right' : 'left' }">{{ t('rideConnectedUsersTitle') }}</h5>

    <div v-if="loading" class="text-center py-3">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">{{ t('loading') }}</span>
      </div>
    </div>

    <div v-else-if="users.length === 0" class="text-muted pb-5" :class="isHebrew ? 'text-right' : 'text-left'">
      {{ t('rideConnectedUsersEmpty') }}
    </div>

    <div v-else>
      <div
        v-for="user in users"
        :key="user.email"
        class="user-row"
      >
        <div class="user-info" :class="isHebrew ? 'text-right' : 'text-left'">
          <div class="user-email">
            {{ primaryLabel(user) }}
            <span v-if="!user.is_active" class="blocked-badge ml-2">{{ t('rideConnectedUsersBlockedBadge') }}</span>
          </div>
          <div class="user-meta">
            <span class="user-auth">{{ authLabel(user) }}</span>
            <!-- The phone is the only real way to reach a WhatsApp traveller,
                 so it sits beside the provider instead of the address, which
                 for them is the undeliverable wa.<phone>@whatsapp.invalid. -->
            <span v-if="showPhoneInMeta(user)" class="user-phone" dir="ltr">· +{{ user.phone }}</span>
            <span class="user-date">· {{ formatDate(user.connected_at) }}</span>
          </div>
        </div>

        <div class="user-actions">
          <span v-if="user.successMessage" class="text-success small action-msg">{{ user.successMessage }}</span>
          <template v-if="user.is_active">
            <b-button
              size="sm"
              variant="outline-danger"
              class="action-btn"
              @click="openBlockModal(user)"
            >
              {{ t('rideConnectedUsersBlockBtn') }}
            </b-button>
            <b-button
              v-if="user.auth_provider === 'email'"
              size="sm"
              variant="outline-secondary"
              class="action-btn"
              :title="t('rideConnectedUsersResetPasswordTooltip')"
              @click="resetPassword(user)"
            >
              {{ t('rideConnectedUsersResetPasswordBtn') }}
            </b-button>
          </template>
          <template v-else>
            <b-button
              size="sm"
              variant="outline-success"
              class="action-btn"
              @click="restoreAccess(user)"
            >
              {{ t('rideConnectedUsersRestoreBtn') }}
            </b-button>
          </template>
        </div>
      </div>
    </div>

    <!-- Block confirmation modal -->
    <b-modal
      v-model="blockModal.show"
      :title="t('rideConnectedUsersBlockModalTitle')"
      :ok-title="t('rideConnectedUsersBlockModalConfirmBtn')"
      :cancel-title="t('rideConnectedUsersBlockModalCancelBtn')"
      ok-variant="danger"
      :dir="isHebrew ? 'rtl' : 'ltr'"
      @ok="confirmBlock"
    >
      <p :class="isHebrew ? 'text-right' : 'text-left'">{{ t('rideConnectedUsersBlockModalBody') }}</p>
      <b-form-checkbox v-if="rideAccesskeyIsActive" v-model="blockModal.blockLink" :class="['mb-2', isHebrew ? 'text-right' : 'text-left']">
        {{ t('rideConnectedUsersBlockModalLinkCheckbox') }}
      </b-form-checkbox>
      <p class="text-muted small mb-0" :class="isHebrew ? 'text-right' : 'text-left'">{{ t('rideConnectedUsersBlockModalBillingNote') }}</p>
    </b-modal>
  </div>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';
import { mapGetters, mapActions } from 'vuex';
import { connectedUserLabel } from '@/utils/sharedRideWarning';
import manageRidePrivacyApi from '@/api/manageRidePrivacy';
import appauthApi from '@/api/appauth';

export default {
  name: 'RideConnectedUsers',
  mixins: [translationMixin],
  props: {
    rideId: { type: [Number, String], required: true },
  },
  data() {
    return {
      loading: true,
      users: [],
      blockModal: {
        show: false,
        user: null,
        blockLink: false,
      },
    };
  },
  computed: {
    ...mapGetters(['apptoken', 'rideAccesskeyIsActive']),
  },
  async mounted() {
    await this.fetchUsers();
  },
  methods: {
    ...mapActions(['showModal']),
    async fetchUsers() {
      this.loading = true;
      try {
        const res = await manageRidePrivacyApi.getRideConnectedUsers(this.apptoken, this.rideId);
        this.users = (res.data.users || []).map(u => ({
          ...u,
          successMessage: '',
        }));
      } catch (e) {
        this.users = [];
      } finally {
        this.loading = false;
      }
    },
    openBlockModal(user) {
      this.blockModal.user = user;
      this.blockModal.blockLink = false;
      this.blockModal.show = true;
    },
    async confirmBlock() {
      const item = this.blockModal.user;
      if (!item) return;
      try {
        this.showModal('loadingIcon');
        await manageRidePrivacyApi.revokeUserRideAccess(
          this.apptoken, this.rideId, item.email, this.blockModal.blockLink
        );
        const idx = this.users.findIndex(u => u.email === item.email);
        if (idx !== -1) {
          this.$set(this.users[idx], 'is_active', false);
          this.$set(this.users[idx], 'successMessage', this.t('rideConnectedUsersBlockSuccess'));
        }
        if (this.blockModal.blockLink) {
          this.$store.commit('setRideAccesskeyIsActive', false);
        }
      } catch (e) {
        // silent
      } finally {
        this.showModal('');
      }
    },
    async restoreAccess(item) {
      try {
        this.showModal('loadingIcon');
        await manageRidePrivacyApi.restoreUserRideAccess(this.apptoken, this.rideId, item.email);
        const idx = this.users.findIndex(u => u.email === item.email);
        if (idx !== -1) {
          this.$set(this.users[idx], 'is_active', true);
          this.$set(this.users[idx], 'successMessage', this.t('rideConnectedUsersRestoreSuccess'));
        }
      } catch (e) {
        // silent
      } finally {
        this.showModal('');
      }
    },
    async resetPassword(item) {
      try {
        this.showModal('loadingIcon');
        await appauthApi.requestPasswordReset(item.email, window.location.origin);
        const idx = this.users.findIndex(u => u.email === item.email);
        if (idx !== -1) {
          this.$set(this.users[idx], 'successMessage', this.t('rideConnectedUsersResetPasswordSuccess'));
        }
      } catch (e) {
        // silent
      } finally {
        this.showModal('');
      }
    },
    primaryLabel(user) {
      // A WhatsApp traveller's email is synthetic and undeliverable, so showing
      // it would be worse than useless -- the manager would try to write to it.
      // Their name is the identity that means something; fall back to the phone
      // when WhatsApp gave us no profile name, and only then to the address.
      return connectedUserLabel(user);
    },
    showPhoneInMeta(user) {
      // When WhatsApp gave us no profile name the phone is already the headline,
      // so repeating it on the line below just prints the same number twice.
      return !!user.phone && this.primaryLabel(user) !== `+${user.phone}`;
    },
    authLabel(user) {
      if (user.auth_provider === 'whatsapp') return this.t('rideConnectedUsersAuthWhatsapp');
      if (user.auth_provider === 'google') return this.t('rideConnectedUsersAuthGoogle');
      return this.t('rideConnectedUsersAuthEmail');
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.ride-connected-users {
  border-top: 1px solid #dee2e6;
  padding-top: 1.5rem;
  padding-bottom: 4rem;
}

.user-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 1rem;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-email {
  font-weight: 500;
  word-break: break-all;
}

.user-meta {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 2px;
}

.user-phone {
  margin-inline-start: 4px;
  font-family: monospace;
  /* dir="ltr" keeps the digits in order inside the RTL row, but the element
     still needs to sit inline with the text around it. */
  display: inline-block;
}

.user-date {
  margin-inline-start: 4px;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  padding-inline-start: 2rem;
}

.action-btn {
  white-space: nowrap;
}

.action-msg {
  white-space: nowrap;
}

.blocked-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #dc3545;
  background-color: #fce8ea;
  border-radius: 4px;
  padding: 1px 6px;
  vertical-align: middle;
}
</style>
